import { jsx as _jsx } from "react/jsx-runtime";
import Typography from '@mui/material/Typography';
import React from 'react';
import { extractSystemStyles } from '@famly/mf_system_system-props';
import { useDataProps } from '@famly/mf_ts-utils_data-aria-props';
import { getEmphasizedFontWeight } from '@famly/mf_theme';
/**
 * Vertical room kept around the trimmed cap→baseline band for the glyph parts that fall outside it
 * (descenders, ascender overshoot, diacritics). `ellipsis` sets `overflow: hidden`, which clips to
 * the padding box, so without this the trim would cut the tail off a "g". The matching negative
 * margin takes the room back out of the layout box, leaving the cap band as what gets centred.
 */
const CAP_HEIGHT_TRIM_ALLOWANCE = '0.4em';
const defaultComponent = (variant) => {
    switch (variant) {
        case 'h1':
            return 'h1';
        case 'h2':
            return 'h2';
        case 'h3':
            return 'h3';
        case 'h4':
            return 'h4';
        case 'h5':
            return 'h5';
        case 'h6':
            return 'h6';
        case 'body':
            return 'p';
        case 'body-small':
            return 'p';
        case 'subtitle':
            return 'p';
        case 'micro':
            return 'p';
        default:
            return 'p';
    }
};
export const Text = (props) => {
    const { variant, children, emphasized, ellipsis, numeric, lines, component: componentFromProp, id, trimToCapHeight, ...rest } = props;
    const component = componentFromProp !== null && componentFromProp !== void 0 ? componentFromProp : defaultComponent(variant);
    const dataProps = useDataProps(rest);
    const { sx: systemSx } = extractSystemStyles(rest);
    // Ensure we always have a valid variant for MUI Typography
    const validVariant = variant || 'body';
    const sx = React.useCallback(theme => {
        return {
            ...systemSx(theme),
            fontWeight: emphasized ? getEmphasizedFontWeight(validVariant) : undefined,
            ...getNumericStyle(numeric, validVariant),
            ...(!ellipsis &&
                lines && {
                display: '-webkit-box',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: lines,
            }),
            ...(trimToCapHeight && {
                textBoxTrim: 'trim-both',
                textBoxEdge: 'cap alphabetic',
                paddingBlock: CAP_HEIGHT_TRIM_ALLOWANCE,
                marginBlock: `-${CAP_HEIGHT_TRIM_ALLOWANCE}`,
            }),
        };
    }, [systemSx, emphasized, validVariant, numeric, ellipsis, lines, trimToCapHeight]);
    return (_jsx(Typography, { component: component, sx: sx, variant: validVariant, noWrap: ellipsis, ...dataProps, id: id, children: children }));
};
const getNumericStyle = (isNumeric, variant) => {
    if (!isNumeric) {
        return {};
    }
    // For subtitle and micro, keep their Inter font but add numeric features
    if (variant === 'subtitle' || variant === 'micro') {
        return {
            fontVariantNumeric: 'lining-nums tabular-nums stacked-fractions slashed-zero',
            fontFeatureSettings: "'ss02' on, 'ss04' on, 'cv11' on, 'ss01' on, 'cv04' on",
        };
    }
    // For other variants (including undefined), switch to Inter variable font and add numeric features
    return {
        fontFamily: 'Inter',
        fontVariantNumeric: 'lining-nums tabular-nums stacked-fractions slashed-zero',
        fontFeatureSettings: "'ss02' on, 'ss04' on, 'cv11' on, 'ss01' on, 'cv04' on",
        fontVariationSettings: "'wght' 400",
    };
};
